import { Link } from "@tanstack/react-router";
import {
  ShoppingCart,
  Factory,
  DollarSign,
  Gauge,
  Package,
  Layers,
  Truck,
  FileText,
  AlertTriangle,
  ClipboardList,
  ArrowUpRight,
  BarChart3,
  Scissors,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { cn, formatDateTime } from "@/lib/utils";
import { RComboChart, fmtShort } from "./Charts";
import { KpiStatCard } from "@/components/shared/KpiStatCard";
import { getStatusLabel } from "@/config/status-registry";

interface OverviewTabProps {
  isLoading: boolean;
  kpis: any;
  quickStats: any;
  monthlyTrend: any[];
  activities: any[];
  earlyWarnings?: any;
  onDrillDown?: (config: { type: string; title: string; status?: string }) => void;
}

export function OverviewTab({
  isLoading,
  kpis,
  quickStats,
  monthlyTrend,
  activities,
  earlyWarnings,
  onDrillDown,
}: OverviewTabProps) {
  const prodAlerts = earlyWarnings?.production || {};
  const delayedPOCount = prodAlerts.delayedPOCount ?? 0;
  const stoppedMachinesCount = prodAlerts.stoppedMachinesCount ?? 0;
  const scrapRate = prodAlerts.scrapRate ?? 0;
  const paperSurplusCount = prodAlerts.paperSurplusCount ?? 0;

  return (
    <div className="space-y-3">
      {/* ═══ THANH CẢNH BÁO VẬN HÀNH 4 CHIỀU (Tối Giản — Không Subtitle) ═══ */}
      <div className="flex flex-wrap items-center gap-2 p-2.5 bg-rose-500/10 border border-rose-500/20 rounded-xl text-xs">
        <span className="font-bold text-rose-700 flex items-center gap-1.5 shrink-0">
          <AlertTriangle className="h-3.5 w-3.5" />
          Cảnh báo vận hành:
        </span>
        <div className="flex flex-wrap items-center gap-2 text-foreground">
          {delayedPOCount > 0 ? (
            <Badge
              variant="outline"
              onClick={() =>
                onDrillDown?.({
                  type: "delayed_pos",
                  title: "Lệnh SX chậm tiến độ",
                })
              }
              className="bg-card border-rose-300 text-rose-800 font-semibold cursor-pointer hover:bg-rose-100 transition-colors"
            >
              {`🚨 ${delayedPOCount} Lệnh SX chậm tiến độ`}
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-card border-emerald-300 text-emerald-700 font-semibold">
              ✓ Lệnh SX đúng tiến độ
            </Badge>
          )}

          {stoppedMachinesCount > 0 ? (
            <Badge
              variant="outline"
              onClick={() =>
                onDrillDown?.({
                  type: "stopped_machines",
                  title: "Máy dừng hoạt động",
                })
              }
              className="bg-card border-border text-foreground font-semibold cursor-pointer hover:bg-muted transition-colors"
            >
              {`⚠️ ${stoppedMachinesCount} Máy dừng / bảo trì`}
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-card border-border text-foreground font-semibold">
              ✓ Thiết bị ổn định
            </Badge>
          )}

          <Badge
            variant="outline"
            className={cn(
              "bg-card font-semibold",
              scrapRate > 2.0
                ? "border-amber-300 text-amber-700"
                : "border-emerald-300 text-emerald-700"
            )}
          >
            {scrapRate > 0 ? `▲ Phế phẩm: ${scrapRate}%` : `✓ Phế phẩm: 0%`}
          </Badge>

          {paperSurplusCount > 0 && (
            <Badge
              variant="outline"
              onClick={() =>
                onDrillDown?.({
                  type: "paper_surplus",
                  title: "Phôi in dôi dư lưu kho",
                })
              }
              className="bg-card border-amber-300 text-amber-800 font-semibold cursor-pointer hover:bg-amber-100 transition-colors"
            >
              📦 {paperSurplusCount} Mã phôi in dôi dư
            </Badge>
          )}
        </div>
      </div>

      {/* ═══ 4 THẺ KPI CHÍNH (Ground Truth — Chuẩn PoP & KpiStatCard) ═══ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiStatCard
          title="Doanh thu"
          value={kpis?.revenue?.value || 0}
          prevValue={kpis?.revenue?.prevRevenue}
          deltaPct={kpis?.revenue?.pct}
          diff={kpis?.revenue?.diff}
          formatType="currencyShort"
          colorVariant="emerald"
          icon={DollarSign}
          href="/orders"
          direction="positive"
          isLoading={isLoading}
          testId="kpi-revenue"
        />

        <KpiStatCard
          title="Đơn hàng"
          value={kpis?.orders?.value ?? 0}
          prevValue={kpis?.orders?.prevOrdersCount}
          deltaPct={kpis?.orders?.pct}
          diff={kpis?.orders?.diff}
          unit="đơn"
          formatType="number"
          colorVariant="blue"
          icon={ShoppingCart}
          href="/orders"
          direction="positive"
          isLoading={isLoading}
          testId="kpi-orders"
        />

        <KpiStatCard
          title="Đang sản xuất"
          value={kpis?.production?.value ?? 0}
          unit="lệnh"
          formatType="number"
          colorVariant="amber"
          icon={Factory}
          badgeText={kpis?.production?.trend || "Đang chạy"}
          href="/production-orders"
          direction="positive"
          isLoading={isLoading}
          testId="kpi-production"
        />

        <KpiStatCard
          title="Hiệu suất OEE"
          value={kpis?.efficiency?.value ?? 0}
          formatType="percent"
          colorVariant="violet"
          icon={Gauge}
          badgeText={kpis?.efficiency?.trend || "0% phế phẩm"}
          href="/shopfloor/loss-time-analytics"
          direction="positive"
          isLoading={isLoading}
          testId="kpi-oee"
        />
      </div>

      {/* ═══ 6 THẺ CHỈ SỐ NHANH ═══ */}
      {!isLoading && quickStats && (
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-2">
          {[
            {
              title: "Tổng ĐH",
              value: quickStats.ordersCount ?? 0,
              color: "blue",
              icon: Package,
              path: "/orders",
            },
            {
              title: getStatusLabel("ProductionOrderStatus", "IN_PROGRESS"),
              value: quickStats.productionOrdersCount ?? 0,
              color: "amber",
              icon: Factory,
              path: "/production-orders",
            },
            {
              title: "Gia công",
              value: quickStats.outsourceCount ?? 0,
              color: "orange",
              icon: Layers,
              path: "/outsource-orders",
            },
            {
              title: "Giao hàng",
              value: quickStats.deliveryCount ?? 0,
              color: "teal",
              icon: Truck,
              path: "/delivery",
            },
            {
              title: "BG chờ duyệt",
              value: quickStats.pendingQuotations ?? 0,
              color: "violet",
              icon: FileText,
              path: "/quotations",
            },
            {
              title: "VT sắp hết",
              value: quickStats.lowStockAlerts ?? 0,
              color: "rose",
              icon: AlertTriangle,
              path: "/material-warehouse",
            },
          ].map((card) => {
            const Icon = card.icon;
            return (
              <Link key={card.title} to={card.path} className="block">
                <div className="bg-card rounded-lg shadow-xs border border-border overflow-hidden cursor-pointer hover:shadow-sm transition-shadow">
                  <div className={`h-0.5 bg-${card.color}-500`} />
                  <div className="px-2.5 py-2">
                    <div className="flex items-center gap-1.5">
                      <Icon className={`w-3.5 h-3.5 text-${card.color}-500`} />
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase truncate">
                        {card.title}
                      </p>
                    </div>
                    <p className={`text-lg font-bold text-foreground tabular-nums mt-1`}>
                      {card.value}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* ═══ BIỂU ĐỒ XU HƯỚNG & HOẠT ĐỘNG (Tối giản - Không subtitle) ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Combo Chart: Monthly Revenue Trend */}
        <div className="bg-card rounded-xl shadow-xs border border-border p-3 lg:col-span-2">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-bold text-foreground uppercase tracking-wide flex items-center gap-1.5">
              <BarChart3 className="w-3.5 h-3.5 text-blue-500" /> Xu hướng 6 tháng
            </h3>
          </div>
          {isLoading ? (
            <Skeleton className="h-[220px] w-full" />
          ) : (
            <RComboChart data={monthlyTrend} />
          )}
        </div>

        <div className="space-y-3">
          {/* Quick Access */}
          <Card className="bg-card border-border shadow-xs">
            <CardHeader className="py-2.5 px-3">
              <CardTitle className="text-xs font-bold text-foreground uppercase tracking-wide flex items-center gap-1.5">
                <Layers className="w-3.5 h-3.5 text-violet-500" /> Truy cập nhanh
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1 px-3 pb-3">
              {[
                {
                  title: "Tạo đơn hàng",
                  icon: ShoppingCart,
                  href: "/orders/create",
                  color: "bg-indigo-50 text-indigo-600",
                },
                {
                  title: "Tạo báo giá",
                  icon: FileText,
                  href: "/quotations/create",
                  color: "bg-violet-50 text-violet-600",
                },
                {
                  title: "Lệnh sản xuất",
                  icon: Factory,
                  href: "/production-orders",
                  color: "bg-amber-50 text-amber-600",
                },
                {
                  title: "Cấp phát giấy in",
                  icon: Scissors,
                  href: "/planning/paper-allocation",
                  color: "bg-emerald-50 text-emerald-600",
                },
                {
                  title: "Giao hàng",
                  icon: Truck,
                  href: "/delivery",
                  color: "bg-sky-50 text-sky-600",
                },
              ].map((link) => {
                const Icon = link.icon;
                return (
                  <Link
                    key={link.title}
                    to={link.href}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/60 transition-colors group"
                  >
                    <div className={cn("p-1.5 rounded-lg shrink-0", link.color)}>
                      <Icon className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-xs font-semibold text-foreground group-hover:text-primary">
                      {link.title}
                    </span>
                    <ArrowUpRight className="w-3.5 h-3.5 text-muted-foreground ml-auto group-hover:text-primary transition-colors" />
                  </Link>
                );
              })}
            </CardContent>
          </Card>

          {/* Recent Activities */}
          <Card className="bg-card border-border shadow-xs">
            <CardHeader className="py-2.5 px-3">
              <CardTitle className="text-xs font-bold text-foreground uppercase tracking-wide flex items-center gap-1.5">
                <ClipboardList className="w-3.5 h-3.5 text-emerald-500" /> Hoạt động gần đây
              </CardTitle>
            </CardHeader>
            <CardContent className="px-3 pb-3">
              {isLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-8 w-full" />
                  ))}
                </div>
              ) : activities.length === 0 ? (
                <div className="text-xs text-muted-foreground font-medium text-center py-4">
                  Chưa có hoạt động nào
                </div>
              ) : (
                <div className="space-y-1">
                  {activities.slice(0, 5).map((act: any, i: number) => (
                    <div
                      key={act.id || i}
                      className="flex items-start justify-between p-1.5 rounded hover:bg-muted/50 text-xs"
                    >
                      <div className="flex items-start gap-2 min-w-0">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0 mt-1.5" />
                        {/* Mô tả xuống tối đa 2 dòng (trước cắt "…" 1 dòng); giờ theo giờ VN, không hiện chuỗi ISO */}
                        <span className="text-foreground font-medium line-clamp-2 break-words" title={act.description || act.title || act.action}>
                          {act.description || act.title || act.action}
                        </span>
                      </div>
                      <span className="text-[10px] text-muted-foreground shrink-0 ml-2 whitespace-nowrap">
                        {formatDateTime(act.time || act.createdAt)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
